<template>
  <div class="sidebar" ref="sidebar">
    <v-layout
      column
      align-center
      fill-height
      justify-start
      style="overflow: auto"
    >
      <v-tooltip bottom color="primary" open-delay="100">
        <template v-slot:activator="{ on }">
          <WeezieLogo class="back-button mt-1 mb-5" @click="backToHub" />
        </template>
        <span style="font-size: 9px">{{ $t("BackToHub") }}</span>
      </v-tooltip>

      <!-- SEARCH -->
      <sidebar-button
        v-if="!isMultiSelect && !isSplicingTable"
        :buttonName="$t('Search')"
        windowName="search"
        windowType="sidebar"
        icon="search"
        @openSidebar="toggleSidebar"
      />

      <!-- LAYERS -->
      <sidebar-button
        v-if="!isMultiSelect && !isSplicingTable"
        :buttonName="$tc('Layer', 2)"
        windowName="layers"
        windowType="sidebar"
        icon="layers"
        @openSidebar="toggleSidebar"
      />

      <!-- CREATE -->
      <sidebar-button
        v-if="!isMultiSelect && !isSplicingTable"
        :buttonName="$t('Toolbar')"
        windowName="create"
        windowType="sidebar"
        icon="construction"
        @openSidebar="toggleTool('create')"
        :disabled="createDisabled"
      />

      <!-- SELECT -->
      <sidebar-button
        v-if="!isMultiSelect && !isSplicingTable"
        :buttonName="$t('Select')"
        windowName="select"
        windowType="sidebar"
        icon="mdi-vector-square"
        @openSidebar="toggleSelect"
      />

      <!-- Measure -->
      <sidebar-button
        v-if="!isMultiSelect && !isSplicingTable"
        :buttonName="$t('MeasureVerb')"
        windowName="measure"
        windowType="sidebar"
        icon="straighten"
        @openSidebar="toggleMeasurementsToolbar"
      />

      <!-- pathInspector -->
      <sidebar-button
        v-if="!isMultiSelect && !isSplicingTable"
        :buttonName="$t('PathInspector')"
        windowName="pathInspector"
        windowType="sidebar"
        icon="mdi-vector-line"
        @openSidebar="togglePathInspectorToolbar"
      />

      <!-- Processing -->
      <sidebar-button
        :buttonName="$t('Processing')"
        windowName="processing"
        windowType="sidebar"
        icon="engineering"
        v-if="hasProcessingPermission && !isMultiSelect && !isSplicingTable"
        @openSidebar="toggleProcessing"
      />

      <!-- PRINT -->
      <sidebar-button
        v-if="!isMultiSelect && !isSplicingTable"
        :buttonName="$t('MapCapture')"
        windowName="print"
        windowType="toolbar"
        icon="photo_camera"
        :disabled="isToTakePrint"
        @openSidebar="togglePrintMap('openPrint', $event)"
      />

      <!-- Heatmap -->
      <sidebar-button
        v-if="!isBidding && !isMultiSelect && !isSplicingTable"
        :buttonName="'Heatmap'"
        windowName="heatmap"
        windowType="toolbar"
        icon="landslide"
        @openSidebar="toggleHeatmap"
      />

      <!-- Work Order -->
      <sidebar-button
        v-if="workOrderPermissions && !isMultiSelect && !isSplicingTable"
        :buttonName="'Work Order'"
        windowName="workOrder"
        windowType="sidebar"
        icon="assignment"
        @openSidebar="toggleSelectWorkOrder"
      />

      <!-- Asset Reservation -->
      <sidebar-button
        v-if="
          userHasFibercloudPermission('asset_reservation', 'store') &&
            userHasFibercloudPermission('asset_reservation_object', 'store') &&
            userHasFibercloudPermission('asset_reservation_template', 'list') &&
            userHasModulePermission('module_asset_reservation') &&  !isBidding && !isMultiSelect  && !isSplicingTable
        "
        :buttonName="$tc('AssetReservation', 1)"
        windowName="createAssetReservation"
        windowType="sidebar"
        icon="mdi-script-text-outline"
        @openSidebar="toggleAssetReservation"
      />

      <v-layout align-end id="badge" class="mb-1">
        <div>
          <v-badge
            :content="messages.length > 9 ? '9+' : messages.length"
            :value="messages.length"
            color="accent"
            tile
            bordered
            dark
            overlap
          >
            <v-btn icon @click="onNotificationButtonClick">
              <v-icon color="#767676" size="20">notifications_none</v-icon>
            </v-btn>
          </v-badge>
          <user-button
            @click.native="toggleUserWindow()"
            avatarWidth="40px"
            avatarHeight="40px"
            :name-is-disable="false"
            style="height: fit-content;"
          />
          <user-window left="68px" bottom="41px" v-show="userWindowOpen" />
        </div>
        <notifications-window
          left="60px"
          bottom="44px"
          :arrowLeft="true"
          v-show="notificationWindow"
          :notificationOpen="notificationWindow"
          @notifications="setNotificationNumber"
        />
      </v-layout>
    </v-layout>

    <transition name="fadeRightSideBar">
      <search
        v-if="openSidebar === 'search' && sidebarEnabled"
        @close="closeSidebar"
      />
    </transition>

    <transition name="fadeRightSideBar">
      <layers
        v-show="openSidebar === 'layers' && sidebarEnabled"
        @close="closeSidebar"
      />
    </transition>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import WeezieLogo from "@/assets/logos/WeezieLogo.vue";

import SidebarButton from "./SidebarButton";

import Search from "./SidebarWindows/Search";
import Layers from "./SidebarWindows/Layers/Layers";
import ArrowSVG from "@/assets/sidebar/closeWindowArrow.svg";
import { bus } from "@/main";
import SelectIcon from "@/assets/home/toolbars/select.svg";
import UserButton from "@/components/User/Navbar/UserButton";
import UserWindow from "@/components/User/Navbar/UserWindow/UserWindow";

export default {
  name: "SideBar",

  components: {
    UserWindow,
    UserButton,
    WeezieLogo,
    SidebarButton,

    Search,
    Layers,

    ArrowSVG,

    SelectIcon
  },

  mixins: [checkPermissionsMixin],

  props:{

    isMultiSelect: {
      type: Boolean,
      default: false
    },

    isSplicingTable:{
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      openPrint: false,
      openTrace: false,
      userWindowOpen: false,
      notificationWindow: false,
      messages: []
    };
  },

  computed: {
    openSidebar() {
      return this.$store.getters.getCurrentlyOpenHomeSidebar;
    },

    openedSidebar() {
      return this.openSidebar !== "";
    },

    sidebarEnabled() {
      return this.$store.getters.isHomeSidebarEnabled;
    },

    isToTakePrint() {
      return !!this.$store.getters.isToTakePrint;
    },

    selectedAction() {
      return this.$store.getters.tools_selectedAction;
    },

    avatar() {
      return this.$store.getters.currentUserImage;
    },
    isBidding(){
      return sessionStorage.getItem("is_bidding") === 'true';
    },

    workOrderPermissions() {
      return (
        this.userHasFibercloudPermission("workorder", "show") ||
        this.userHasFibercloudPermission("workorder", "update") ||
        this.isSuperAdmin
      ) && !this.isBidding && this.userHasFibercloudPermission("workorder", "store");
    },
    hasProcessingPermission(){
      return this.userHasFibercloudPermission("processing");
    },
    /**
     * Defines whether the create button is disabled or not
     * @returns {boolean}
     */
    createDisabled() {
      return !(
        this.userHasFibercloudPermission("building_store") ||
        this.userHasFibercloudPermission("pathway_store") ||
        this.userHasFibercloudPermission("technical_point_store") ||
        this.userHasFibercloudPermission("closure_store") ||
        this.userHasFibercloudPermission("technical_site_store") ||
        this.userHasFibercloudPermission("technical_local_store") ||
        this.userHasFibercloudPermission("cable_store") ||
        this.userHasFibercloudPermission("optical_line_terminations_store") ||
        this.userHasFibercloudPermission("fiber_distribution_point_store") ||
        this.userHasFibercloudPermission("closure_zone_store") ||
        this.userHasFibercloudPermission("annotation_zone_store") ||
        this.userHasFibercloudPermission("annotation_point_store") ||
        this.userHasFibercloudPermission("cluster_store") ||
        this.userHasFibercloudPermission("slack_store")
      );
    },

    /**
     * defines whether the measure tool is selected or not
     * @returns {any}
     */
    showMeasurementsToolbar() {
      return this.$store.getters.isMeasurementsToolbarOpen;
    },

    /**
     * defines whether the heatmap tool is selected or not
     * @returns {any}
     */
    showHeatmapToolbar() {
      return this.$store.getters.isShowHeatmap;
    }
  },

  methods: {
    ...mapActions([
      "setOpenHomeSidebar",
      "setHomeToolsSidebarState",
      "setHomePrintState",
      "SET_TOOL_ACTION",
      "SET_TOOL_OBJECT_LAYER",
      "setMeasurementsToolbarState",
      "setShowHeatmap",
      "setFullscreen",
      "SET_TOOL_TECHNICAL_TYPE"
    ]),

    /**
     * Back To Hub
     * @returns {Promise<void>}
     */
    async backToHub() {
      await this.setFullscreen(false);
      await this.$router.push({ name: "hub" });
    },

    toggleSidebar(selectedSidebar) {
      this.setOpenHomeSidebar(selectedSidebar);
    },

    /**
     * Toggle the active tool.
     */
    toggleTool(toolName) {
      if (this.selectedAction === "create") {
        this.deactivateCreate();
      }
      if (this.selectedAction === "pathInspector") {
        this.deactivatePathInspector();
      }

      if (this.selectedAction === toolName) {
        this.SET_TOOL_ACTION("");
        this.toggleSidebar("");
      } else {
        bus.$emit("close-object-select-form");
        this.SET_TOOL_ACTION(toolName);
        this.toggleSidebar(toolName);
      }

      this.SET_TOOL_OBJECT_LAYER("");
    },

    /**
     * Toggles the select tool.
     */
    toggleSelect() {
      let toolName = "select";
      this.toggleSidebar(toolName);
      if (this.selectedAction === toolName) {
        this.deactivateSelect();
      }
      if (this.showMeasurementsToolbar) {
        this.setMeasurementsToolbarState(!this.showMeasurementsToolbar);
      }
      this.toggleTool(toolName);
    },
    toggleSelectWorkOrder() {
      let toolName = "workOrder";
      this.toggleTool(toolName);
    },

    /**
     * Deactivate select tool
     */
    deactivateSelect() {
      bus.$emit("reset-area");
      bus.$emit("enable-click-events");
    },

    /**
     * Toggles the Measurements tool.
     */
    toggleMeasurementsToolbar() {
      if (this.selectedAction === "create" || this.selectedAction === "") {
        this.setMeasurementsToolbarState(!this.showMeasurementsToolbar);
      } else {
        this.toggleTool("");
        this.setMeasurementsToolbarState(!this.showMeasurementsToolbar);
      }
    },

    /**
     * Toggles the Path Inspector tool.
     */
    togglePathInspectorToolbar() {
      this.toggleTool("pathInspector");
      if (this.showMeasurementsToolbar) {
        this.setMeasurementsToolbarState(!this.showMeasurementsToolbar);
      }
    },

    /**
     * Deactivate path inspector tool
     */
    deactivatePathInspector() {
      bus.$emit("close-windows-path-inspector");
    },

    /**
     * Toggles the generate tool.
     */
    toggleProcessing() {
      this.toggleTool("processing");
      if (this.showMeasurementsToolbar) {
        this.setMeasurementsToolbarState(!this.showMeasurementsToolbar);
      }
    },

    /**
     * Deactivate create tool
     */
    deactivateCreate() {
      bus.$emit("deselect-selected-object-to-create");
    },

    /**
     * Toggles the heatmap tool.
     */
    toggleHeatmap() {
      this.setShowHeatmap(!this.showHeatmapToolbar);
    },

    togglePrintMap(window, open) {
      if (!this.isToTakePrint) {
        this.setHomePrintState(open);
      }
    },

    closeSidebar() {
      this.toggleSidebar("");
    },

    closeUserWindow() {
      this.userWindowOpen = false;
    },

    /**
     * Opens userWindow and closes notificationWindow if open.
     */
    toggleUserWindow() {
      this.userWindowOpen = !this.userWindowOpen;
      this.notificationWindow = false;
    },

    /**
     * Opens notification window and closes userWindow if open.
     */
    onNotificationButtonClick() {
      this.notificationWindow = !this.notificationWindow;
      this.userWindow = false;
    },

    /**
     * @param {Array} - Array of notifications
     * Checks if the readAt is null, if so, the notifications get filtered to message data property to use with vuetify badge
     */
    setNotificationNumber(data) {
      this.messages = data.filter(item => !item.readAt);
    },

    /**
     * Toggles the asset reservation tool
     */
    toggleAssetReservation() {
      let previousLayer = this.$store.getters.tools_selectedAction;
      this.toggleTool("createAssetReservation");
      if (previousLayer === "createAssetReservation") {
        this.SET_TOOL_OBJECT_LAYER("");
        bus.$emit("enable-click-events")
      } else {
        this.SET_TOOL_OBJECT_LAYER("fc_asset_reservation");
        this.SET_TOOL_TECHNICAL_TYPE(null);
      }
    }
  }
};
</script>

<style scoped>
.sidebar {
  background: white;
  position: absolute;
  width: 50px;
  height: 100%;
  z-index: 200;
  box-shadow: var(--sidebar-shadow);
}

.back-button-container {
  width: 100%;
  height: 30px;
  cursor: pointer;
}

.back-button {
  cursor: pointer;
}

.toolsMovedRight {
  left: 290px;
}
.toolsMovedRightOfLayers {
  left: 240px;
}

.fadeRightSideBar-enter-active,
.fadeRightSideBar-leave-active {
  transition: all 0.3s;
}
.fadeRightSideBar-enter,
.fadeRightSideBar-leave-to {
  /* transform: translateX(-20px); */
  opacity: 0;
}

#badge >>> .v-badge__badge {
  color: black !important;
  font-weight: 600 !important;
  font-size: 13px;
  padding: 1px !important;
  min-width: 16px !important;
  height: 16px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/SideBar/SideBar.vue